import { useState, useEffect, useCallback } from 'react';
import { 
  subscribeUser, 
  unsubscribeUser, 
  getSubscriptionStatus, 
  getUserData 
} from '../services/websocketApi/user-subscription';
import websocketService from '../services/websocket';

/**
 * Custom hook for subscribing to and managing user data
 * @param {boolean} autoSubscribe - Whether to automatically subscribe on mount
 * @returns {Object} User data state and management functions
 */
const useUserData = (autoSubscribe = true) => {
  const [userData, setUserData] = useState(getUserData());
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handler for user data updates
  const handleUserDataUpdate = useCallback((data) => {
    setUserData(data);
  }, []);

  // Function to subscribe to user data
  const subscribe = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await subscribeUser();
      // Check for successful subscription
      if (response && response.status === 'success') {
        setIsSubscribed(true);
        
        // Initialize with current data if available
        const currentData = getUserData();
        if (currentData) {
          setUserData(currentData);
        }
      } else {
        setError('Failed to subscribe: Unknown error');
      }
      
      return response;
    } catch (err) {
      setError(err.message || 'Failed to subscribe to user data');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to unsubscribe from user data
  const unsubscribe = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await unsubscribeUser();
      // Check for successful unsubscription
      if (response && response.status === 'success') {
        setIsSubscribed(false);
      } else {
        setError('Failed to unsubscribe: Unknown error');
      }
      return response;
    } catch (err) {
      setError(err.message || 'Failed to unsubscribe from user data');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to check subscription status
  const checkSubscriptionStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await getSubscriptionStatus();
      // Update subscription status based on response
      if (response) {
        setIsSubscribed(response.isSubscribed);
      } else {
        setError('Failed to get subscription status: Unknown error');
      }
      return response;
    } catch (err) {
      setError(err.message || 'Failed to get subscription status');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Set up listener for user data updates
  useEffect(() => {
    websocketService.on('user-cache-updated', handleUserDataUpdate);
    
    return () => {
      websocketService.off('user-cache-updated', handleUserDataUpdate);
    };
  }, [handleUserDataUpdate]);

  // Auto-subscribe on mount if enabled
  useEffect(() => {
    if (autoSubscribe) {
      subscribe();
    }
    
    // Cleanup: unsubscribe on unmount
    return () => {
      if (isSubscribed) {
        unsubscribe();
      }
    };
  }, [autoSubscribe, subscribe, unsubscribe, isSubscribed]);

  return {
    userData,
    isSubscribed,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    checkSubscriptionStatus
  };
};

export default useUserData; 